// The current project (ARCHITECTURE-v3 2.1). The URL (/p/:projectId/...) is
// the truth; entering a project calls POST /api/me/project, which checks
// access (404 when the person may not see it) and makes it the session's
// current project, so emails, "/" and search default to it.
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext } from "react";
import type { MeV3, ProjectList, ProjectRef } from "@convenor/shared/v3";
import { can, type Action, type ProjectRole } from "./permissions";
import { api } from "./client";

const LAST_PROJECT = "convenor.lastProject";

export function rememberProject(id: string): void {
  try {
    localStorage.setItem(LAST_PROJECT, id);
  } catch {
    /* private mode: nothing to remember */
  }
}

export function lastProject(): string | null {
  try {
    return localStorage.getItem(LAST_PROJECT);
  } catch {
    return null;
  }
}

export type ProjectListItem = ProjectRef & { status: string; startDate: string | null; endDate: string | null };

/** The projects the signed-in person can open (header switcher, redirects). */
export function useProjectList(enabled = true) {
  return useQuery({
    queryKey: ["project-list"],
    queryFn: () => api.get<ProjectList>("/api/projects"),
    select: (d): ProjectListItem[] => [
      // Open projects by name (as before), whatever the session's current project is.
      ...[...d.open].sort((x, y) => x.name.localeCompare(y.name)).map((p) => ({ id: p.id, name: p.name, reference: p.reference, status: "open", startDate: p.period.start, endDate: p.period.end })),
      ...d.closed.map((p) => ({ id: p.id, name: p.name, reference: p.reference, status: "closed", startDate: null, endDate: null })),
    ],
    enabled,
    staleTime: 60_000,
  });
}

/** Opens a project: access check + current-project switch. 404 = no access. */
export function useProjectAccess(projectId: string | undefined) {
  return useQuery({
    queryKey: ["project-access", projectId],
    queryFn: () => api.post<MeV3>("/api/me/project", { projectId }),
    enabled: Boolean(projectId),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export type ProjectScope = {
  projectId: string;
  project: ProjectRef;
  role: ProjectRole;
  isLead: boolean;
  /** The v3 Me returned by the switch (user, org, expert profile, unread count). */
  me: MeV3;
  can: (action: Action) => boolean;
};

export const ProjectContext = createContext<ProjectScope | null>(null);

/** Inside /p/:projectId/*: the project, the person's role there and can(). */
export function useProject(): ProjectScope {
  const p = useContext(ProjectContext);
  if (!p) throw new Error("useProject() is only available inside a /p/:projectId route.");
  return p;
}

/** Like useProject(), but null outside a project (header, global pages). */
export function useOptionalProject(): ProjectScope | null {
  return useContext(ProjectContext);
}

export function makeScope(projectId: string, me: MeV3): ProjectScope {
  const project = me.project ?? { id: projectId, name: "", reference: "", role: "viewer" as const };
  const role = project.role;
  const isLead = role === "lead";
  return {
    projectId,
    project: { id: project.id, name: project.name, reference: project.reference },
    role,
    isLead,
    me,
    can: (action) => can(role, action, { isLead }),
  };
}

/** OB-F20 (round 4): a draft project's name (the onboarding's drafts in the project list), or null. Top bar and tab titles. */
export function useDraftName(draftId: string | null): string | null {
  const q = useQuery({
    queryKey: ["project-list"],
    queryFn: () => api.get<ProjectList>("/api/projects"),
    select: (d) => d.drafts?.find((x) => x.id === draftId)?.name ?? null,
    enabled: !!draftId,
    staleTime: 60_000,
  });
  return q.data ?? null;
}
